<template>
    <div class="costunit-list-table">
        <b-table v-if="tableItems.length" :fields="fields" :items="tableItems" responsive striped>
            <template v-slot:table-colgroup="scope">
                <col
                    v-for="field in scope.fields"
                    :key="field.key"
                    :style="{ width: field.width ?? 'auto' }">
            </template>
            <template v-slot:cell(costUnitText)="data">
                <span class="font-weight-bold">{{ emptyValuePlaceholder(data.value) }}</span>
                <span v-if="data.item.sourceText">
                    <br>
                    ({{ data.item.sourceText }})
                </span>
            </template>
            <template v-slot:cell(dateReceipt)="data">
                {{ formatDate(data.value) }}
            </template>
            <template v-slot:cell(amount)="data">
                <csp-record-detail-cost-units-amount-table
                    amount-property-prefix="amount"
                    :cost-unit="data.item" show-insurance-figure-label/>
            </template>
            <template v-slot:cell(paymentSums)="data">
                <csp-record-detail-cost-units-amount-table v-if="!data.item.isDraft"
                    amount-property-prefix="paymentsSum"
                    :cost-unit="data.item" is-payment sum-positive-class="text-success"/>
            </template>
            <template v-slot:cell(datePaid)="data">
                <template v-if="!data.item.isDraft && data.item.latestPayment.data">
                    {{ formatDate(data.item.latestPayment.data.attributes.datePaid) }}
                </template>
            </template>
            <template v-slot:cell(amountOpen)="data">
                <csp-record-detail-cost-units-amount-table v-if="!data.item.isDraft"
                    amount-property-prefix="amountOpen"
                    :cost-unit="data.item" highlight-insurance-payment sum-positive-class="text-danger">
                    <template v-slot:amountPrefix>
                        <span v-if="data.item.hasAmountAdjustment">*</span>
                    </template>
                </csp-record-detail-cost-units-amount-table>
            </template>
            <template v-slot:cell(comment)="data">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span style="white-space: pre-wrap" v-html="data.value"/>
                <div v-if="data.item.isDraft" class="alert alert-warning inline-block mt-1">
                    Bitte prüfen - Schadenpositionsdaten aus Rechnungsscan nicht eindeutig
                </div>
            </template>
            <template v-slot:cell(actions)="data">
                <div class="table-actions mb-2" style="text-align: left">
                    <a href="#" title="Details anzeigen"
                        @click.prevent="$emit('cost-unit-details', data.item.id)">
                        <i class="inticon-autoclaim_detail"/>
                    </a>

                    <a v-if="data.item.permissions.canAddCarDealerPayment" href="#" title="Zahlung hinzufügen"
                        @click.prevent="$emit('cost-unit-add-car-dealer-payment', data.item.id)">
                        <i class="inticon-autoclaim_hinzufugen"/>
                    </a>
                    <a v-if="data.item.permissions.canEdit || data.item.permissions.canEditPaymentsCarDealer"
                        href="#" title="Bearbeiten"
                        @click.prevent="$emit('cost-unit-edit', data.item.id)">
                        <i class="inticon-autoclaim_bearbeiten"/>
                    </a>
                    <a v-if="data.item.permissions.canDelete" href="#" title="Löschen"
                        @click.prevent="$emit('cost-unit-delete', data.item.id)">
                        <i class="inticon-autoclaim_loschen"/>
                    </a>
                    <a v-if="data.item.isOcrCreated" href="#" title="Verifizieren"
                        @click.prevent="$emit('cost-unit-verify', data.item.id)">
                        <i class="inticon-autoclaim_done"/>
                    </a>
                </div>
                <div v-if="data.item.isOcrCreated" class="mb-1" style="text-align: left;">
                    via KI erfasst
                </div>
                <div v-if="data.item.isOcrConfirmed" class="mb-1" style="text-align: left;">
                    bestätigte KI-Erfassung
                </div>
                <csp-hide-for-group
                    v-if="hideForGroupOptions.length > 0"
                    :default-value="data.item.hideForGroup"
                    :options="hideForGroupOptions"
                    :record-id="recordId"/>
            </template>
        </b-table>
        <b-alert v-if="costUnits.length == 0" show variant="info">
            Keine Schadenpositionen vorhanden.
        </b-alert>
    </div>
</template>

<script>
    import {emptyValuePlaceholder} from '@js/functions/format';
    import {formatDate} from '@js/functions/date';
    import CostUnitAmountTable from '@desktop/components/record/cost-unit/RecordCostUnitAmountTable.vue';

    export default {
        components: {
            'csp-record-detail-cost-units-amount-table': CostUnitAmountTable,
        },
        props: {
            costUnits: {
                type: Array,
                required: true,
            },
            costUnitDrafts: {
                type: Array,
                required: true,
            },
            hideForGroupOptions: {
                type: Array,
                default: null,
            },
        },
        emits: [
            'cost-unit-add-car-dealer-payment',
            'cost-unit-delete',
            'cost-unit-details',
            'cost-unit-edit',
            'cost-unit-verify',
        ],
        data() {
            const recordId = parseInt(this.$route.params.recordId);
            return {
                recordId,
                fields: [
                    {
                        key: 'receipt',
                        label: 'Beleg Nr.',
                        thClass: 'align-top',
                        class: '',
                        formatter: emptyValuePlaceholder,
                        width: '120px',
                    },
                    {
                        key: 'costUnitText',
                        label: 'Bezeichnung',
                        thClass: 'align-top',
                        width: '180px',
                    },
                    {
                        key: 'dateReceipt',
                        label: 'Beziffert am',
                        class: 'text-center',
                        thClass: 'align-top text-center',
                        width: '100px',
                    },
                    {
                        key: 'amount',
                        label: 'Forderung',
                        thClass: 'align-top text-center',
                        width: '220px',
                    },
                    {
                        key: 'paymentSums',
                        label: 'Zahlung',
                        thClass: 'align-top text-center',
                        width: '150px',
                    },
                    {
                        key: 'datePaid',
                        label: 'Bezahlt am',
                        class: 'text-center',
                        thClass: 'align-top text-center',
                        width: '100px',
                    },
                    {
                        key: 'amountOpen',
                        label: 'Offen',
                        thClass: 'align-top text-center',
                        width: '150px',
                    },
                    {
                        key: 'comment',
                        label: 'Bemerkung',
                        thClass: 'align-top',
                        tdClass: '',
                        formatter: emptyValuePlaceholder,
                    },
                    {
                        key: 'actions',
                        label: 'Aktionen / Sichtbarkeit',
                        thClass: 'align-top',
                        tdClass: 'text-right',
                        formatter: emptyValuePlaceholder,
                        width: '100px',
                    },
                ],
            }
        },
        computed: {
            tableItems() {
                const tableItems = [];

                // regular costUnits
                this.costUnits.forEach((item) => {
                    tableItems.push(
                        {
                            id: item.id,
                            isDraft:false,
                            ...item.attributes,
                            ...item.meta,
                        }
                    );
                });

                // costUnitDrafts
                this.costUnitDrafts.forEach((draft) => {
                    tableItems.push({
                        id: draft.id,
                        isDraft: true,
                        ...draft.attributes,
                        ...draft.meta,
                    });
                });

                return tableItems;
            },
        },
        methods: {
            emptyValuePlaceholder,
            formatDate,
        },
    }
</script>
